// Email service integration for SOVO Development
// This file contains utilities for sending emails via SendGrid

import { paymentReminderTemplate, paymentConfirmationTemplate } from './emailTemplates';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Initialize Firebase Functions (europe-central2 region for better latency in Poland)
const functions = getFunctions(undefined, 'europe-central2');
const sendEmailFunction = httpsCallable(functions, 'sendEmail');

// Email sending function using Firebase Functions
// metadata is optional and can include: type, investmentId, flatId, flatCode, customerId, customerName, paymentId, invoiceNumber, stageNumber
export const sendEmail = async (to, subject, htmlContent, textContent, attachments = [], metadata = {}) => {
  try {
    const result = await sendEmailFunction({
      to,
      subject,
      htmlContent,
      textContent,
      attachments,
      metadata
    });

    console.log('Email sent successfully:', result.data);
    return result.data;
  } catch (error) {
    console.error('Error sending email:', error);
    throw error;
  }
};

// Payment reminder email sender
export const sendPaymentReminder = async (customerData, paymentData) => {
  const {
    email,
    name: customerName
  } = customerData;

  const {
    apartmentCode,
    dueAmount,
    dueDate,
    installmentNumber,
    contractNumber
  } = paymentData;

  // Calculate days overdue
  const today = new Date();
  const dueDateObj = new Date(dueDate);
  const timeDiff = today.getTime() - dueDateObj.getTime();
  const daysOverdue = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));

  const template = paymentReminderTemplate({
    customerName,
    apartmentCode,
    dueAmount,
    dueDate: dueDateObj.toLocaleDateString('pl-PL'),
    installmentNumber,
    daysOverdue,
    contractNumber
  });

  return await sendEmail(
    email,
    template.subject,
    template.html,
    template.text
  );
};

// Payment confirmation email sender
export const sendPaymentConfirmation = async (customerData, paymentData, invoiceAttachment = null) => {
  const {
    email,
    name: customerName
  } = customerData;

  const {
    apartmentCode,
    paidAmount,
    paymentDate,
    installmentNumber,
    contractNumber,
    invoiceNumber,
    remainingAmount = 0
  } = paymentData;

  const template = paymentConfirmationTemplate({
    customerName,
    apartmentCode,
    paidAmount,
    paymentDate: new Date(paymentDate).toLocaleDateString('pl-PL'),
    installmentNumber,
    contractNumber,
    invoiceNumber,
    remainingAmount
  });

  const attachments = [];
  if (invoiceAttachment) {
    attachments.push({
      content: invoiceAttachment.content, // Base64 encoded content
      filename: `faktura_${invoiceNumber}.pdf`,
      type: 'application/pdf',
      disposition: 'attachment'
    });
  }

  return await sendEmail(
    email,
    template.subject,
    template.html,
    template.text,
    attachments
  );
};

// Batch reminder sender for multiple overdue payments
export const sendBatchPaymentReminders = async (overduePayments) => {
  const results = [];
  
  for (const payment of overduePayments) {
    try {
      const result = await sendPaymentReminder(payment.customer, payment.payment);
      results.push({ success: true, paymentId: payment.id, result });
    } catch (error) {
      results.push({ success: false, paymentId: payment.id, error: error.message });
    }
  }
  
  return results;
};

// Email template preview function (for testing)
export const previewTemplate = (templateType, data) => {
  switch (templateType) {
    case 'payment-reminder':
      return paymentReminderTemplate(data);
    case 'payment-confirmation':
      return paymentConfirmationTemplate(data);
    default:
      throw new Error(`Unknown template type: ${templateType}`);
  }
};

// Validate email data before sending
export const validateEmailData = (customerData, paymentData) => {
  const requiredCustomerFields = ['email', 'name'];
  const requiredPaymentFields = ['apartmentCode', 'dueAmount', 'installmentNumber', 'contractNumber'];

  for (const field of requiredCustomerFields) {
    if (!customerData[field]) {
      throw new Error(`Missing required customer field: ${field}`);
    }
  }

  for (const field of requiredPaymentFields) {
    if (!paymentData[field]) {
      throw new Error(`Missing required payment field: ${field}`);
    }
  }

  // Validate email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(customerData.email)) {
    throw new Error('Invalid email address format');
  }

  return true;
};